import Step from 'components/global/Step'
import { useErrorMessages } from 'utils/hooks/useErrorMessages'
import { useEffect } from 'react'
import { STEPS, CREATOR_ACCOMMODATION_STEPS } from 'routes/steps'
import { useAccommodation } from 'store/accommodation-slice/hooks'
import { useAppDispatch, useAppSelector } from 'store/hooks'
import { FieldError } from 'store/order-slice'
import { useApp } from 'store/steps'
import AllGuestDetails from './sections/AllGuestDetails'
import BookingPerson from './sections/BookingPerson'
import SpecialRequests from './sections/SpecialRequests'
import { useIntl } from 'react-intl'
import { setIsErrorModalOpened } from 'store/general-slice'
import {
    setIsPackageConflictModalOpened,
    setIsSeniorConflictModalOpen,
} from 'store/ticket-slice'
import { CityBreakBox } from 'components/common/CityBreak/components/CityBreakBox'
import useCityBreak from 'components/common/CityBreak/utils/useCityBreak'

const AccommodationDetails = () => {
    const { bungalows } = useAppSelector((state) => state.accommodation)
    const { editAccommodationInCart, isAccommodationLoading } =
        useAccommodation(bungalows[0]?.room?.productId)
    const { navigateToStep, stepIndex, isCreatorAccommodation } = useApp()
    const { setErrorMessages, clearErrorMessages } = useErrorMessages()
    const { formatMessage } = useIntl()
    const dispatch = useAppDispatch()
    const { isCityBreakApplicableForAccommo } = useCityBreak()

    const steps = isCreatorAccommodation ? CREATOR_ACCOMMODATION_STEPS : STEPS

    const handleEditAccommodationInCart = async () => {
        const messages: FieldError[] = []

        const validateField = (value: string, id: string) => {
            value === '' &&
                messages.push({
                    property_path: id,
                    message: formatMessage({
                        id: 'global.validation.empty_field',
                        defaultMessage: 'To pole nie powinno być puste',
                    }),
                })
        }

        bungalows.forEach((bungalow, bungalowIndex) => {
            bungalow.adults.forEach(({ name, surname }, index) => {
                validateField(name, `adults-${index}-name-${bungalowIndex}`)
                validateField(
                    surname,
                    `adults-${index}-surname-${bungalowIndex}`
                )
            })
            bungalow.kids.forEach(({ name, surname }, index) => {
                validateField(name, `kids-${index}-name-${bungalowIndex}`)
                validateField(surname, `kids-${index}-surname-${bungalowIndex}`)
            })
            bungalow.infants.forEach(({ name, surname }, index) => {
                validateField(name, `infants-${index}-name-${bungalowIndex}`)
                validateField(
                    surname,
                    `infants-${index}-surname-${bungalowIndex}`
                )
            })
        })

        if (messages.length > 0) {
            const error = { messages }
            setErrorMessages(error)
            throw error
        }

        try {
            await editAccommodationInCart()
        } catch (error) {
            if ('message' in (error as Error)) {
                const { message } = error as Error

                if (message.includes('Senior Offer Conflict')) {
                    dispatch(setIsSeniorConflictModalOpen(true))
                    throw error
                }

                if (message.includes('Conflict')) {
                    dispatch(setIsPackageConflictModalOpened(true))
                    throw error
                }
            } else {
                const { messages } = error as {
                    messages: FieldError[]
                }

                if (
                    messages?.length &&
                    messages.some((error) =>
                        error.property_path.includes('detail')
                    )
                )
                    throw error
            }
            // if there are no specific single message or messages assigned to fields
            // - end with general, global error modal
            dispatch(setIsErrorModalOpened(true))
        }
    }

    useEffect(() => {
        if (bungalows.filter((bungalow) => !!bungalow.room).length === 0) {
            navigateToStep({ stepId: steps[stepIndex - 1].id })
        }
    }, [bungalows.length])

    useEffect(() => {
        clearErrorMessages()
    }, [])

    return (
        <Step
            title={formatMessage({
                id: 'global.steps.caption_accommo',
                defaultMessage: 'Nocleg',
            })}
            onButtonClick={handleEditAccommodationInCart}
            onSkipButtonClick={handleEditAccommodationInCart}
            isButtonLoading={isAccommodationLoading}
        >
            <BookingPerson />
            <AllGuestDetails />
            <SpecialRequests />
            {isCityBreakApplicableForAccommo && <CityBreakBox />}
        </Step>
    )
}

export default AccommodationDetails
